// File: src/pages/master/SuperAdminList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

// ---- your existing helpers / APIs
import { _GetList } from "../../import/ApiEndPoint/ApiCalling";
import { Super_Admin_List } from "../../import/ApiEndPoint/Api";
import { CheckPermission } from "../../import/CheckPermission";
import { exportDataToPDF } from "../../import/Downloader";

// ---------- tiny page-scoped CSS (same vibe as WareHouseList) ----------
const injectOnce = () => {
  if (document.getElementById("sa-inline-css")) return;
  const style = document.createElement("style");
  style.id = "sa-inline-css";
  style.innerHTML = `
    .sa-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .sa-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .sa-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px }
    .sa-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .sa-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .sa-table tbody tr:hover{ background:#f9fbff; }
  `;
  document.head.appendChild(style);
};

// ---------- helpers ----------
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const planTypeText = (n) => (Number(n) === 1 ? "Yearly" : "One Time");

export default function SuperAdminList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);

  // permissions
  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
    Edit: true,
    Delete: true,
  });

  // ------- STATIC column (always visible, not in chooser) -------
  const companyCol = useMemo(
    () => ({
      key: "companyName",
      header: "COMPANY NAME",
      get: (r) => r?.companyName,
      render: (r) => {
        const id = r?._id;
        const name = r?.companyName || "-";
        return (
          <span
            style={{ color: "#0b5fff", fontWeight: 700, cursor: "pointer" }}
            title={name}
            onClick={() => navigate(`/app/master/superadmins/${id}`)}
          >
            {name}
          </span>
        );
      },
    }),
    [navigate]
  );

  // ------- BASE columns (controlled by chooser) -------
  const baseColumns = useMemo(
    () => [
      { key: "firstName", header: "FIRST NAME", get: (r) => r?.firstName },
      { key: "email", header: "EMAIL", get: (r) => r?.email },
      { key: "mobileNumber", header: "MOBILE", get: (r) => r?.mobileNumber },
      { key: "pincode", header: "PINCODE", get: (r) => r?.pincode },
      { key: "Aadhar_No", header: "AADHAR NO", get: (r) => r?.Aadhar_No },
      { key: "Pan_No", header: "PAN NO", get: (r) => r?.Pan_No },
      { key: "City", header: "CITY", get: (r) => r?.City },
      { key: "State", header: "STATE", get: (r) => r?.State },
      { key: "planStatus", header: "PLAN STATUS", get: (r) => r?.planStatus },
      {
        key: "subscriptionPlan.planName",
        header: "PLAN NAME",
        get: (r) => r?.subscriptionPlan?.planName,
      },
      {
        key: "subscriptionPlan.subscriptionType",
        header: "PLAN TYPE",
        get: (r) => planTypeText(r?.subscriptionPlan?.subscriptionType),
      },
      { key: "billAmount", header: "PLAN PRICE", get: (r) => r?.billAmount },
      { key: "planStart", header: "PLAN START", get: (r) => r?.planStart },
      { key: "planEnd", header: "PLAN END", get: (r) => r?.planEnd },
      {
        key: "userAllotted",
        header: "USERS ALLOTTED",
        get: (r) => r?.userAllotted,
      },
      { key: "PassportNo", header: "PASSPORT NO", get: (r) => r?.PassportNo },
      { key: "address", header: "ADDRESS", get: (r) => r?.address },
      {
        key: "last_job_firm_name",
        header: "FIRM NAME",
        get: (r) => r?.last_job_firm_name,
      },
      { key: "address1", header: "ADDRESS 1", get: (r) => r?.address1 },
      { key: "address2", header: "ADDRESS 2", get: (r) => r?.address2 },
    ],
    []
  );

  // map for quick lookup
  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    // NOTE: companyName is NOT included here on purpose (static, outside chooser)
    return m;
  }, [baseColumns]);

  // column chooser (improved)
  const STORAGE_KEY = "SuperAdminListCols";
  const addBufferRef = useRef(new Set()); // use a Set for reliability
  const [modal, setModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedKeys, setSelectedKeys] = useState([]); // visible (non-static) columns

  useEffect(() => {
    injectOnce();

    // permissions
    try {
      const inside = CheckPermission("SuperAdmin List");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }
    } catch {}

    // restore columns (exclude companyName if ever stored)
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const allKeys = baseColumns.map((c) => c.key);

      let next = [];
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => colMap.has(k) && k !== "companyName");
      }
      if (!next.length) next = allKeys; // default to all base columns

      setSelectedKeys(next);
    } catch {
      setSelectedKeys(baseColumns.map((c) => c.key));
    }
  }, [baseColumns, colMap]);

  // fetch rows
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await _GetList(Super_Admin_List);
        const list = Array.isArray(res?.SuperAdmin) ? res.SuperAdmin : [];
        const active = list.filter((s) => s?.status === "Active");
        setRows(active);
      } catch (e) {
        console.error(e);
        setRows([]);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load Super Admins."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // visible columns (STATIC first + selected)
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [companyCol, ...cols]; // company name always first
  }, [selectedKeys, colMap, companyCol]);

  // search (exclude company name from search as per earlier ask)
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter(
      (c) => c.key !== "companyName" && c.key !== "__actions__"
    );
    return rows.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  // exports
  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols.map((c) => `"${toStr(c.get(r)).replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "SuperAdminList");
  };
  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // -------- Column Chooser Modal (fixed logic) --------
  const openModal = () => {
    addBufferRef.current = new Set();
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  // available columns: baseColumns - selectedKeys
  const availableCols = useMemo(() => {
    const selectedSet = new Set(selectedKeys);
    return baseColumns.filter((c) => !selectedSet.has(c.key));
  }, [baseColumns, selectedKeys]);

  const handleCheckbox = (checked, colKey) => {
    const buf = new Set(addBufferRef.current);
    if (checked) {
      buf.add(colKey);
    } else {
      buf.delete(colKey);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    if (!addBufferRef.current.size) return;
    const toAdd = Array.from(addBufferRef.current);
    const next = Array.from(new Set([...selectedKeys, ...toAdd]));
    setSelectedKeys(next);
    addBufferRef.current = new Set();
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedKeys));
    closeModal();
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="sa-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Super Admin List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("SuperAdminList")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No super admins found.
              </div>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.email}>
                        {visibleCols.map((c) => (
                          <td key={c.key} title={toStr(c.get(r))}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal (fixed logic) === */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            {/* Available Columns (only those not currently visible) */}
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {availableCols.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
                {!availableCols.length && (
                  <div style={{ color: "#6b7280", padding: "6px 2px" }}>
                    No more columns to add
                  </div>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            {/* Visible Columns (reorder/remove) */}
            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {/* Static column hint */}
                  <div
                    style={{
                      ...pickItemStyle,
                      background: "#f4f7f6",
                      fontWeight: 700,
                    }}
                    title="Always visible"
                  >
                    {companyCol.header} (static)
                  </div>

                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                        title="Click to select; use ↑ ↓ or Remove"
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* lil styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
